/**
 * The messaging pipeline, family by family - the `queues` check's snapshot rendered.
 *
 * This panel exists to answer "what is the pipeline actually doing?", so it is mostly
 * a table: there are six families, four lanes each, and consumer counts alongside -
 * far past the point where more colours would help. The stacked bar above it carries
 * the shape at a glance; the table carries the numbers.
 *
 * Three things the broker reports that read as problems and are not, and which this
 * panel is careful to label rather than colour red:
 *
 * - **A dead or repair queue has no consumer.** A dead queue is moved by its own TTL
 *   and a repair queue is terminal, so zero consumers there is the healthy state.
 * - **`report_scheduling` is drained by polling.** It has no `basic_consume` consumer
 *   by design, and its escalation lanes do not exist at all.
 * - **`standby: null` is not `standby: 0`.** Counting standbys needs the RabbitMQ
 *   management API; when it is unreachable the spares are unseen, not absent. Printing
 *   0 there would claim there are no hot spares when the truth is unknown.
 */

import { ChevronDown, Info } from 'lucide-react'
import { useState } from 'react'

import type { QueueFamily, QueueLane, QueueSnapshot } from '../api/types'
import { cn } from '../lib/cn'
import { EM_DASH, formatDelay, humanise } from '../lib/format'
import { PipelineBar } from './charts/PipelineBar'
import { Tooltip } from './ui/Tooltip'

interface QueuePanelProps {
  snapshot: QueueSnapshot
  className?: string
}

export function QueuePanel({ snapshot, className }: QueuePanelProps) {
  const families = Object.entries(snapshot.families ?? {})
  const totals = snapshot.totals ?? {}
  const standbyKnown = totals.consumers_standby !== null && totals.consumers_standby !== undefined

  /**
   * The per-family table is detail, not glance, so it starts closed - six families of
   * four lanes each is what makes a healthy card twice the height of every other one,
   * which in a grid drives a large void beside the short cards. The bar and the
   * consumer counts above it stay visible, and anything actually wrong opens it.
   */
  const anythingWrong = families.some(([, family]) => {
    const primary = family.primary
    const attached = primary?.consumers?.attached ?? 0
    const polled = primary?.drained_by === 'polling'
    return (
      primary?.declared === false ||
      (!polled && attached < 1) ||
      (primary?.messages ?? 0) > 0 ||
      (family.repair?.messages ?? 0) > 0 ||
      (family.retry?.messages ?? 0) > 0
    )
  })
  const [showFamilies, setShowFamilies] = useState(anythingWrong)

  return (
    <section className={cn('space-y-4', className)} aria-label="Message queues">
      <PipelineBar totals={totals} title="Messages by escalation lane" />

      {/* Consumers: emphasis, not categorical - active in the accent, standby grey. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
        <span className="font-semibold text-ink-secondary">Consumers</span>

        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: 'var(--color-accent)' }}
          />
          <span className="text-ink-secondary">active</span>
          <span className="tabular font-semibold text-ink">
            {totals.consumers_active ?? EM_DASH}
          </span>
        </span>

        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: 'var(--color-axis)' }}
          />
          <span className="text-ink-secondary">standby</span>
          {standbyKnown ? (
            <span className="tabular font-semibold text-ink">{totals.consumers_standby}</span>
          ) : (
            <Tooltip content="Counting standby consumers needs the RabbitMQ management API, which was unreachable. The spares are unseen, not absent.">
              <span className="cursor-help font-semibold text-ink-muted underline decoration-dotted">
                unknown
              </span>
            </Tooltip>
          )}
        </span>

        <span className="text-ink-muted">
          {totals.consumers_attached ?? EM_DASH} attached
          {totals.consumers_source ? ` · via ${totals.consumers_source.replace(/_/g, ' ')}` : ''}
        </span>
      </div>

      {families.length === 0 ? (
        <p className="text-xs text-ink-muted">The snapshot listed no queue families.</p>
      ) : !showFamilies ? (
        <button
          type="button"
          onClick={() => setShowFamilies(true)}
          aria-expanded={false}
          className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-xs text-ink-secondary hover:bg-surface-sunken"
        >
          <span>
            {families.length} queue famil{families.length === 1 ? 'y' : 'ies'} — depths,
            consumers and escalation lanes
          </span>
          <ChevronDown size={14} aria-hidden />
        </button>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[42rem] border-collapse text-xs">
            <caption className="sr-only">
              Queue families with their primary depth, consumers, and escalation lanes
            </caption>
            <thead>
              <tr className="border-b text-left text-ink-muted">
                <th scope="col" className="py-2 pr-3 font-medium">
                  Family
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Queued
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Consumers
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Retrying
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Cooling off
                </th>
                <th scope="col" className="py-2 pr-3 font-medium">
                  Parked
                </th>
                <th scope="col" className="py-2 font-medium">
                  <span className="sr-only">Detail</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {families.map(([name, family]) => (
                <FamilyRow key={name} name={name} family={family} />
              ))}
            </tbody>
          </table>

          <button
            type="button"
            onClick={() => setShowFamilies(false)}
            aria-expanded
            className="mt-2 inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink"
          >
            <ChevronDown size={13} aria-hidden className="rotate-180" />
            Hide queue families
          </button>
        </div>
      )}
    </section>
  )
}

function FamilyRow({ name, family }: { name: string; family: QueueFamily }) {
  const [open, setOpen] = useState(false)
  const primary = family.primary
  const consumers = primary?.consumers
  const polled = primary?.drained_by === 'polling'

  return (
    <>
      <tr className="border-b align-top">
        <th scope="row" className="py-2.5 pr-3 text-left font-medium text-ink">
          {humanise(name)}
          <span className="block font-normal text-ink-muted">{primary?.queue}</span>
        </th>

        <td className="tabular py-2.5 pr-3">
          <Depth value={primary?.messages ?? null} declared={primary?.declared} />
        </td>

        <td className="py-2.5 pr-3">
          {polled ? (
            <Tooltip content="This queue is drained by a polling scheduler rather than an attached consumer, so no consumer here is the normal state.">
              <span className="cursor-help text-ink-muted underline decoration-dotted">
                polled
              </span>
            </Tooltip>
          ) : (
            <ConsumerGlyphs consumers={consumers} />
          )}
        </td>

        <td className="tabular py-2.5 pr-3">
          <LaneCell lane={family.retry} absent="no retry lane" />
        </td>
        <td className="tabular py-2.5 pr-3">
          <LaneCell lane={family.dead} absent="no dead lane" />
        </td>
        <td className="tabular py-2.5 pr-3">
          <LaneCell lane={family.repair} absent="no repair lane" />
        </td>

        <td className="py-2.5 text-right">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-ink-secondary hover:bg-surface-sunken"
          >
            <span className="sr-only">
              {open ? 'Hide' : 'Show'} lane detail for {humanise(name)}
            </span>
            <ChevronDown
              size={14}
              aria-hidden
              className={cn('transition-transform', open && 'rotate-180')}
            />
          </button>
        </td>
      </tr>

      {open ? (
        <tr className="border-b">
          <td colSpan={7} className="px-0 py-3">
            <div
              className="grid gap-3 rounded-md p-3 sm:grid-cols-2 xl:grid-cols-4"
              style={{ backgroundColor: 'var(--color-surface-sunken)' }}
            >
              <LaneDetail title="Primary" lane={primary} />
              <LaneDetail title="Retry" lane={family.retry} />
              <LaneDetail title="Dead (cooling off)" lane={family.dead} />
              <LaneDetail title="Repair (parked)" lane={family.repair} />
            </div>

            {consumers?.instances && consumers.instances.length > 0 ? (
              <ul className="mt-3 space-y-1.5">
                {consumers.instances.map((instance) => (
                  <li key={instance.tag ?? instance.channel} className="flex items-start gap-2">
                    <span
                      aria-hidden
                      className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{
                        backgroundColor: instance.active
                          ? 'var(--color-accent)'
                          : 'var(--color-axis)',
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-ink">
                        {instance.active ? 'Active' : 'Standby'}
                        {instance.status ? (
                          <span className="text-ink-muted"> · {instance.status}</span>
                        ) : null}
                      </p>
                      <p className="truncate text-[11px] text-ink-muted">
                        {instance.channel ?? EM_DASH}
                        {instance.tag ? ` · ${instance.tag}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** A queue depth, or a note that the queue was never declared. */
function Depth({ value, declared }: { value: number | null; declared?: boolean }) {
  if (declared === false) {
    return (
      <Tooltip content="The queue has not been declared — the topology was never set up, or the queue was deleted.">
        <span className="cursor-help font-semibold text-critical underline decoration-dotted">
          not declared
        </span>
      </Tooltip>
    )
  }
  if (value === null) return <span className="text-ink-muted">{EM_DASH}</span>
  return (
    <span className={cn('font-semibold', value > 0 ? 'text-ink' : 'text-ink-muted')}>
      {value}
    </span>
  )
}

function LaneCell({ lane, absent }: { lane: QueueLane | null; absent: string }) {
  if (!lane) {
    return (
      <Tooltip content={`This family has ${absent} — nothing escalates through it.`}>
        <span className="cursor-help text-ink-muted underline decoration-dotted">
          {EM_DASH}
        </span>
      </Tooltip>
    )
  }
  return <Depth value={lane.messages} declared={lane.declared} />
}

/** One lane's specifics: where it returns to, and what moves its messages. */
function LaneDetail({ title, lane }: { title: string; lane: QueueLane | null | undefined }) {
  if (!lane) {
    return (
      <div className="text-[11px] text-ink-muted">
        <p className="font-semibold text-ink-secondary">{title}</p>
        <p className="mt-1">Not configured for this family.</p>
      </div>
    )
  }

  const rows: Array<[string, string]> = [
    ['Queue', lane.queue],
    ['Messages', lane.messages === null ? EM_DASH : String(lane.messages)],
  ]
  if (lane.drained_by) rows.push(['Drained by', lane.drained_by.replace(/_/g, ' ')])
  if (lane.returns_to) rows.push(['Returns to', lane.returns_to])
  if (lane.terminal) rows.push(['Terminal', 'yes — needs a human'])
  if (lane.first_delay_ms !== undefined)
    rows.push(['First delay', formatDelay(lane.first_delay_ms)])
  if (lane.max_delay_ms !== undefined) rows.push(['Max delay', formatDelay(lane.max_delay_ms)])
  if (lane.ttl_ms !== undefined) rows.push(['TTL', formatDelay(lane.ttl_ms)])

  return (
    <div className="min-w-0 text-[11px]">
      <p className="font-semibold text-ink-secondary">{title}</p>
      <dl className="mt-1 space-y-0.5">
        {rows.map(([term, value]) => (
          <div key={term} className="flex gap-1.5">
            <dt className="shrink-0 text-ink-muted">{term}</dt>
            <dd className="min-w-0 truncate text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * Attached consumers as dots - filled accent for the one doing the work, grey for the
 * hot spares. Emphasis rather than categorical colour: the active consumer is the
 * point, the standbys are context.
 */
function ConsumerGlyphs({ consumers }: { consumers?: QueueFamily['primary']['consumers'] }) {
  if (!consumers) return <span className="text-ink-muted">{EM_DASH}</span>

  const { attached, active, standby } = consumers
  if (attached === 0) {
    return (
      <Tooltip content="No consumer is attached to this primary work queue — the worker process is not running.">
        <span className="cursor-help font-semibold text-critical underline decoration-dotted">
          none attached
        </span>
      </Tooltip>
    )
  }

  const activeCount = active ?? 0
  const standbyCount = standby ?? 0

  return (
    <Tooltip
      content={
        <div className="space-y-1">
          <p>
            <span className="font-semibold text-ink">{attached}</span> attached ·{' '}
            <span className="font-semibold text-ink">{activeCount}</span> active
            {standby === null ? (
              <span className="text-ink-secondary">, standby count unknown</span>
            ) : (
              <>
                {' · '}
                <span className="font-semibold text-ink">{standbyCount}</span> standby
              </>
            )}
          </p>
          <p className="text-ink-secondary">
            These queues run with single-active-consumer, so the broker works exactly one
            and holds the rest ready to take over.
          </p>
        </div>
      }
    >
      <span className="inline-flex cursor-help items-center gap-1">
        <span className="inline-flex gap-[3px]">
          {Array.from({ length: Math.min(attached, 8) }, (_, index) => (
            <span
              key={index}
              aria-hidden
              className="inline-block h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  index < activeCount ? 'var(--color-accent)' : 'var(--color-axis)',
              }}
            />
          ))}
        </span>
        <span className="tabular text-ink">
          {activeCount}
          <span className="text-ink-muted">
            /{attached}
            {standby === null ? '' : ''}
          </span>
        </span>
        {standby === null ? <Info size={11} aria-hidden className="text-ink-muted" /> : null}
      </span>
    </Tooltip>
  )
}
